import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import * as logger from '../../../logger';

import * as api from '../../../api';

import { Button } from '../../../components/Button';
import Enum from '../../../components/Question/Enum';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ModalContainer } from '../../../components/ModalContainer';
import { Option } from '../../../components/utils';

import { isEnumMember } from 'common/dist/utils/types';

import defaultStyles from '../../../styles/formSetStyles.module.css';
import styles from './index.module.css';
import { InfoMessage } from '../../../components/InfoMessage';

interface Props {
  isModalOpen: boolean;
  provisionalPatientId: string | null;
  setIsModalOpen: (value: boolean) => void;
  verificationOptions: Option<api.Register.AllowedVerification>[];
  onPatientVerifiedAndLoggedIn: (args: {
    authenticationCode: string;
    reconciliationStatus: api.Register.ReconciliationStatus;
  }) => Promise<void>;
}

const RegistrationModal: React.FC<Props> = (props) => {
  const {
    isModalOpen,
    setIsModalOpen,
    verificationOptions,
    provisionalPatientId,
    onPatientVerifiedAndLoggedIn,
  } = props;

  const { t } = useTranslation();
  const [verificationSource, setVerificationSource] =
    useState<Option<api.Register.AllowedVerification> | null>(null);
  const [sentVerificationCode, setSentVerificationCode] =
    useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSelectionSkipped, setIsSelectionSkipped] = useState(false);

  const sendVerificationCode = useCallback(
    async (
      source?: api.Register.AllowedVerification,
      isSelectionSkipped?: boolean,
    ) => {
      const alertMessage = t(
        'Failed to send a verification code at the moment, please see the front desk.',
      );
      const selectedSource = source?.kind || verificationSource?.value.kind;

      if (!selectedSource || !provisionalPatientId) {
        alert(alertMessage);
        return;
      }

      setIsSubmitting(true);

      try {
        const { success } =
          await api.client.Previsit.requestContactVerification({
            source: selectedSource,
            provisionalPatientId,
          });
        if (success) setSentVerificationCode(true);
        else {
          alert(t('Failed to send verification code, please try again later.'));
          if (isSelectionSkipped) setIsModalOpen(false);
        }
      } catch (e) {
        logger.error(
          e,
          `RegistrationModal.sendVerificationCode : Failed to send verification code`,
        );
        if (verificationOptions.length === 1) {
          setIsModalOpen(false);
        }
        alert(alertMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      provisionalPatientId,
      verificationSource,
      setIsModalOpen,
      t,
      verificationOptions.length,
    ],
  );

  const resendAuthCodeOption = async () => {
    setSentVerificationCode(false);
    setIsSubmitting(false);
  };

  const verifyCodeAndLogin = async () => {
    if (!verificationCode || !provisionalPatientId) {
      alert(t('Failed to verify code, please see the front desk.'));
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await api.client.Previsit.verifyCodeAndLogin({
        code: verificationCode,
        provisionalPatientId,
      });
      await onPatientVerifiedAndLoggedIn(result);
    } catch (e) {
      logger.error(
        e,
        `RegistrationModal.verifyCodeAndLogin : Failed to verify code`,
      );
      alert(t('Invalid verification code.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (verificationOptions.length !== 1) return;
    if (sentVerificationCode) return;
    if (isSelectionSkipped) return;
    if (
      verificationOptions[0].value.kind ===
      api.Register.ContactVerificationSource.Sms
    ) {
      return;
    }
    setIsSelectionSkipped(true);
    setVerificationSource(verificationOptions[0]);
    sendVerificationCode(verificationOptions[0].value, true);
  }, [
    verificationOptions,
    sentVerificationCode,
    sendVerificationCode,
    verificationSource,
    isSelectionSkipped,
  ]);

  let body = (
    <div className={styles.LoadingWrapper}>
      <LoadingSpinner />
    </div>
  );
  if (sentVerificationCode && !isSubmitting) {
    body = (
      <form
        className={classNames(styles.Inputs, styles.RegistrationInputs)}
        onSubmit={(event) => {
          event.preventDefault();
          verifyCodeAndLogin();
        }}
      >
        <div className={classNames(defaultStyles.SubLabel, styles.SubLabel)}>
          <strong className={styles.ModalSubTitle}>Authentication code</strong>
          <div>
            {t('A 6-digit authentication code was sent')}{' '}
            {verificationSource?.display
              ? t('to {{to}}', {
                  to: verificationSource.display,
                })
              : ''}
          </div>
        </div>
        <input
          className={styles.Input}
          type="number"
          value={verificationCode || ''}
          placeholder={t('Verification code')}
          onChange={(e) =>
            e.currentTarget.value.length <= 6 &&
            setVerificationCode(e.currentTarget.value)
          }
          autoComplete="one-time-code"
        />
        <Button
          type="submit"
          children={isSubmitting ? t('Loading...') : t('Continue')}
          disabled={(verificationCode || '').length !== 6 || isSubmitting}
        />
        <Button
          className={styles.ResendCodeOptionButton}
          type="button"
          onClick={resendAuthCodeOption}
          children={t("Didn't receive a code?")}
        />
      </form>
    );
  } else if (
    !isSubmitting &&
    (verificationOptions.length > 1 || !isSelectionSkipped)
  ) {
    body = (
      <div className={classNames(styles.Inputs, styles.RegistrationInputs)}>
        <div className={defaultStyles.SubLabel}>
          <strong>{t('How would you like to verify your identity?')}</strong>
        </div>
        <Enum
          value={verificationSource?.value.kind || ''}
          options={verificationOptions.map((option) => ({
            ...option,
            value: option.value.kind,
          }))}
          onChange={(value) => {
            if (!isEnumMember(api.Register.ContactVerificationSource, value)) {
              return;
            }
            setVerificationSource(
              verificationOptions.find(
                (option) => option.value.kind === value,
              ) || null,
            );
          }}
        />
        {verificationSource?.value.kind ===
        api.Register.ContactVerificationSource.Sms ? (
          <InfoMessage>
            {t(
              'If you previously unsubscribed, you will be resubscribed so we can deliver this verification text message.',
            )}
          </InfoMessage>
        ) : null}
        <div>
          <Button
            className={styles.SubmitButton}
            type="button"
            onClick={() => sendVerificationCode()}
            children={isSubmitting ? t('Loading...') : t('Continue')}
            disabled={
              !verificationSource || !provisionalPatientId || isSubmitting
            }
          />
        </div>
      </div>
    );
  }

  return (
    <ModalContainer
      contentClassName={styles.ModalContainer}
      isOpen={isModalOpen}
      onModalClose={() => setIsModalOpen(false)}
      topLabel={t('Verify your identity')}
    >
      {body}
    </ModalContainer>
  );
};

export default RegistrationModal;
